/*
Defines the jspsych object for the task trial (practice or main). Includes the object which will control sound after response is given in main task.
Data of each trial will be saved in jspsych data on finish of trial. Some session data variables are updated.
*/

/* eslint-disable no-plusplus */
/* eslint-disable prefer-const */
/* eslint-disable arrow-body-style */
import store from "store2"; //storing session data
import { audioResponse } from "./audioFeedback";
import { jsPsych } from "../../taskSetup";
import jsPsychSurveyHtmlForm from "@jspsych/plugin-survey-html-form";
import jsPsychAudioMultiResponse from "@jspsych-contrib/plugin-audio-multi-response";
import jsPsychAudioKeyboardResponse from "@jspsych/plugin-audio-keyboard-response";
import jsPsychCallFunction from "@jspsych/plugin-call-function";
import { mediaAssets } from "../../..";
import { updateProgressBar } from "../helpers/updateProgressBar";
import { shuffle } from "../../shared/helpers/shuffleArray";
import i18next from "i18next";
import { alpacaValidityEvaluator } from "../timeline";
import { startTimerGPT } from "../helpers/updateCountDown";

let rt = [];
let key = [];
let startTime;

// Initialises timers
export const initBlock = (corpusName) => {
  let stim = {
    type: jsPsychCallFunction,
    func: () => {
      // if this is the first trial in this block, we will: update the corpus
      if (store.session("indexTracking") === -1) {
        // will end the block by setting timeOut to be true (used for timer)
        let currentCorpus = store.session.get("corpusAll")[corpusName];
        store.session.set("currentCorpus", currentCorpus);
      }
    },
  };
  return stim;
};

// Non-adaptive testing: gets the next stimulus in the corpus
const getNextStimulus = () => {
  let corpus, nextStimulus, remainingStimuli;

  // read the current version of the corpus
  corpus = store.session.get("currentCorpus");

  // chose the next stimulus
  nextStimulus = corpus[0];
  // get the remaining stimuli
  remainingStimuli = corpus.slice(1);
  // store the item for use in the trial
  store.session.set("nextStimulus", nextStimulus);
  // update the corpus with the remaining unused items
  corpus = remainingStimuli;
  store.session.set("currentCorpus", corpus);
};

export const updateStimulus = () => {
  let stim = {
    type: jsPsychCallFunction,
    func: () => {
      // for keeping track of number of trials completed
      store.session.transact("indexTracking", (oldVal) => oldVal + 1);
      getNextStimulus();
    },
  };
  return stim;
};

const prepareSurveyChoices = (target, distractors) => {
  // randomly select a location for the correct answer
  const randIndex = Math.floor(Math.random() * (distractors.length + 1));

  // randomize the order of the distractors
  const stimulus = shuffle(distractors);
  let choices = [];
  for (let i = 0; i < distractors.length; i++) {
    choices.push(stimulus[i]);
  }

  // insert the target
  choices.splice(randIndex, 0, target);

  return {
    target: target,
    choices: choices,
    correctResponseNum: randIndex,
  };
};

export const numberMultichoiceTimer = (corpusName, assessment_stage_val) => {
  let stim = {
    type: jsPsychAudioMultiResponse,
    response_allowed_while_playing: true,
    data: {
      // Here is where we specify that we should save the trial to Firestore
      assessment_stage: assessment_stage_val,
    },
    stimulus: mediaAssets.audio.nullAudio,
    prompt: () => {
      return `<p class="item-stimulus" id="stimulus_val">${
        store.session.get("nextStimulus").item
      }</p>`;
    },
    prompt_above_buttons: true,
    button_choices: () => {
      // Experiment logic should not be happening in trial parameters
      const stimulus = store.session.get("nextStimulus");
      const target = stimulus.target;
      const d1 = stimulus.distractor1;
      const d2 = stimulus.distractor2;
      const d3 = stimulus.distractor3;

      // use the target letter for the button
      const trialInfo = prepareSurveyChoices(target, [d1, d2, d3]);

      store.session.set("target", target);
      store.session.set("correctResponseNum", trialInfo.correctResponseNum);
      store.session.set("choices", trialInfo.choices);

      return trialInfo.choices;
    },
    button_html: () => {
      return "<button>%choice%</button>";
    },
    on_load: () => {
      const btnOption = store.session.get("config").buttonLayout;
      document
        .getElementById("jspsych-audio-multi-response-btngroup")
        .classList.add(`default-layout`);
    },
    on_finish: (data) => {
      const choices = store.session("choices");
      const stimulus = store.session.get("nextStimulus");

      // check response and record it
      data.correct =
        data.button_response === store.session("correctResponseNum") ? 1 : 0;
      store.session.set("dataCorrect", data.correct);
      store.session.set("response", data.button_response);
      store.session.set("responseValue", choices[data.button_response]);

      let save_trial = true;
      if (store.session.get("timerStatus")) {
        save_trial = !store.session("timeOut");
      }

      jsPsych.data.addDataToLastTrial({
        save_trial: save_trial,
        pid: store.session.get("config").pid,
        subtask: corpusName,
        trialNumBlock: store.session("indexTracking") + 1,
        itemID: stimulus.itemID,
        item_raw: stimulus.item_raw,
        item: stimulus.item,
        target: stimulus.target,
        distractor1: stimulus.distractor1,
        distractor2: stimulus.distractor2,
        distractor3: stimulus.distractor3,
        choices: store.session("choices"),
        correctResponseNum: store.session("correctResponseNum"),
        correct: store.session("dataCorrect"),
      });
    },
  };
  return stim;
};

const blackScreen = {
  type: jsPsychAudioKeyboardResponse,
  stimulus: () => mediaAssets.audio.neutralSoundCut,
  prompt: () => {
    return `<div class="blackBG"></div>`;
  },
  trial_duration: () => 500,
  response_ends_trial: () => false,
};

export const ifTimeoutFlash = {
  timeline: [blackScreen],
  conditional_function: () => {
    if (store.session.get("timeOut")) {
      return true;
    }
    return false;
  },
};

export const numberMainTimer = (corpusName, assessment_stage_val) => {
  let stim = {
    type: jsPsychSurveyHtmlForm,
    html: () => {
      let currentItem = store.session.get("nextStimulus");
      let questionHTML = `<p>` + currentItem.item + `</p>`;
      let responseHTML = ``;
      if (currentItem.addImage) {
        questionHTML +=
          `<img src="${
            mediaAssets.images[
              "alpaca" + currentItem.itemID + currentItem.version
            ]
          }" style="height:` +
          currentItem.image_height +
          `;">`;
      }

      questionHTML = `<div class=question-box>` + questionHTML + `</div>`;
      if (currentItem.response_format[0] === "single") {
        responseHTML =
          `<div class="response-horizontal">
            <input type="text" name="question_input_0" id="question_input_key_0" class="item-textbox-alpaca" style="text-align:center; width:` +
          currentItem.textbox_width +
          `;">
          </div>`;
      } else if (currentItem.response_format[0] === "fraction") {
        responseHTML =
          `<input type="text" name="question_input_0" id="question_input_key_0" class="item-textbox-alpaca" style="text-align:center; width:` +
          currentItem.textbox_width +
          `;">
            <hr>
            <input type="text" name="question_input_1" id="question_input_key_1" class="item-textbox-alpaca" style="text-align:center; width:` +
          currentItem.textbox_width +
          `;">`;
      } else {
        for (let i = 0; i < currentItem.response_format.length; i++) {
          if (currentItem.response_format[i] == "leadIn") {
            responseHTML +=
              `<p>` +
              currentItem.lead[i] +
              `</p>
            <input type="text" name="question_input_` +
              i +
              `" id="question_input_key_` +
              i +
              `" class="item-textbox-alpaca" style="text-align:center; width:` +
              currentItem.textbox_width +
              `;">`;
          } else {
            responseHTML +=
              `<input type="text" name="question_input_` +
              i +
              `" id="question_input_key_` +
              i +
              `" class="item-textbox-alpaca" style="text-align:center; width:` +
              currentItem.textbox_width +
              `;">
            <p>` +
              currentItem.lead[i] +
              `</p>`;
          }
        }
        responseHTML =
          `<div class="response-horizontal">` + responseHTML + `</div>`;
      }

      return (
        `<canvas id="canvas-timer-gpt" width="160" height="160" class="canvas-timer"></canvas>
      <div class=item-stimulus-alpaca>` +
        questionHTML +
        responseHTML +
        `<p class="warning-text" id="warning">${i18next.t("warning")}</p>
      </div>`
      );
    },
    data: {
      // Here is where we specify that we should save the trial to Firestore
      assessment_stage: assessment_stage_val,
    },
    on_start: () => {
      //initialise variables for trial
      rt = [];
      key = [];
      startTime = performance.now(); //get initial time
      const timerId = setTimeout(() => {
        store.session.set("timeOut", true);
        jsPsych.finishTrial();
      }, store.session.get("nextStimulus").time_limit);
      store.session.set("timerId", timerId);
    },
    on_load: () => {
      document.getElementById("question_input_key_0").focus();
      const step = 20;
      const updateInterval = Math.round(1000 / step);
      const totalTime = store.session.get("nextStimulus").countdown_time * step; // Total time in seconds
      const canvas = document.getElementById("canvas-timer-gpt");
      const ctx = canvas.getContext("2d");
      let colorCodes = {
        info: {
          color: "green",
        },
        warning: {
          color: "orange",
          threshold: Math.floor(totalTime / 2),
          transitionEnd: Math.floor(totalTime / 2) - step,
        },
        alert: {
          color: "red",
          threshold: Math.floor(totalTime / 4),
          transitionEnd: Math.floor(totalTime / 4) - step,
        },
      };

      const timerIdCountdown = setTimeout(() => {
        store.session.set("countDownStarted", true);
        //startTimer(store.session.get("nextStimulus").countdown_time);
        startTimerGPT(totalTime, updateInterval, ctx, canvas, colorCodes, step);
      }, store.session.get("nextStimulus").countDownAppears);
      store.session.set("timerIdCountdown", timerIdCountdown);

      //hide the survey form submit button
      const submit_button = document.getElementById(
        "jspsych-survey-html-form-next",
      );
      submit_button.classList.add("hide-submit");

      const preventKeyDown = (event) => {
        event.preventDefault();
      };

      const storeKeyPress = (event) => {
        //only permit numbers, backspace and enter key
        if (event.code === "ArrowLeft" || event.code === "ArrowUp") {
          let element = document.getElementById("question_input_key_0");
          element.focus();
          //element.setSelectionRange(element.value.length, element.value.length);
          element.selectionStart = element.value.length;
          element.selectionEnd = element.value.length;
        } else if (event.code === "ArrowRight" || event.code === "ArrowDown") {
          let element = document.getElementById("question_input_key_1");
          element.focus();
          element.setSelectionRange(element.value.length, element.value.length);
        } else if (
          event.code === "Enter" &&
          !store.session.get("warningVisible") &&
          document.getElementById("question_input_key_1")
        ) {
          let textEmpty0 =
            document.getElementById("question_input_key_0").value.trim() === "";
          let textEmpty1 =
            document.getElementById("question_input_key_1").value.trim() === "";
          if (textEmpty0 != textEmpty1) {
            event.preventDefault();
            document.getElementById("warning").style.visibility = "visible";
            store.session.set("warningVisible", true);
          }
        } else {
          const endTime = performance.now();
          const response_time = Math.round(endTime - startTime);
          const name = event.key;
          key.push(name);
          rt.push(response_time);
        }
      };

      //to prevent long pressing enter key from submitting trial
      function getPromiseFromEvent(item, event) {
        return new Promise((resolve) => {
          const listener = () => {
            item.removeEventListener(event, listener);
            document
              .getElementById("jspsych-survey-html-form")
              .removeEventListener("keydown", preventKeyDown);
            document
              .getElementById("jspsych-survey-html-form")
              .addEventListener("keydown", storeKeyPress);
            resolve();
          };
          item.addEventListener(event, listener);
        });
      }

      async function waitForKeyUp() {
        const form = document.getElementById("jspsych-survey-html-form");
        await getPromiseFromEvent(form, "keyup");
      }

      if (store.session.get("allowKeyUp")) {
        document
          .getElementById("jspsych-survey-html-form")
          .addEventListener("keydown", storeKeyPress);
        store.session.set("allowKeyUp", false);
      } else {
        document
          .getElementById("jspsych-survey-html-form")
          .addEventListener("keydown", preventKeyDown);

        waitForKeyUp();
      }
    },
    on_finish: (data) => {
      clearInterval(store.session.get("intervalId"));
      store.session.set("countDownStarted", false);
      store.session.set("warningVisible", false);

      const stimulus = store.session.get("nextStimulus");

      let response_val = [];
      let correctCount = 0;
      if (data.response) {
        for (let i = 0; i < stimulus.target.length; i++) {
          response_val.push(
            data.response["question_input_" + i].replace(/\s+/g, ""),
          );
          if (data.response["question_input_" + i] === stimulus.target[i]) {
            correctCount++;
          }
        }
      }

      //let correct = parseInt(response_val) === stimulus.target; //when csv is stored locally, targets are in int format
      //let correct = response_val === stimulus.target; //when csv is stored in bucket, targets are in string format
      if (correctCount === stimulus.target.length) {
        store.session.set("dataCorrect", 1); // if response = 1 then the participant got it correct
      } else {
        store.session.set("dataCorrect", 0); // if response = 0 then the participant got it wrong
      }

      // feed response to fluencyValidityEvaluator for evaluation per trial
      alpacaValidityEvaluator.addResponseData(
        data.rt,
        response_val.toString(),
        store.session("dataCorrect"),
      );

      let save_trial = true;
      let time_out = store.session("timeOut");

      jsPsych.data.addDataToLastTrial({
        save_trial: save_trial,
        time_out: time_out,
        corpusName: corpusName,
        pid: store.session.get("config").pid,
        trialNumBlock: store.session("indexTracking") + 1,
        itemID: stimulus.itemID,
        cc_grade_level: stimulus.cc_grade_level,
        time_limit: stimulus.time_limit,
        item_raw: stimulus.item_raw,
        version: stimulus.version,
        target_1: stimulus.target[0],
        target_2: stimulus.target[1] ?? "",
        correct: store.session("dataCorrect"),
        response_1: response_val[0],
        response_2: response_val[1] ?? "",
        respTime: rt,
        keyPress: key,
      });

      // progress bar
      if (corpusName !== "practice") {
        if (save_trial) {
          if (correctCount === stimulus.target.length) {
            store.session.transact("totalCorrect", (oldVal) => oldVal + 1);
          }
          store.session.transact("trialNumTotal", (oldVal) => oldVal + 1);
        }
        updateProgressBar();
      }
    },
  };
  return stim;
};

//add neutral audio feedback
export const ifRealTrialResponse = {
  timeline: [audioResponse],
  conditional_function: () => {
    return true;
  },
};
